import { auth, db } from '../../../firebase/config'
import {
	formatDiscountForSquare,
	formatItemsForFirebase,
	formatItemsForSquare,
	formatServiceChargesForSquare,
	getRestaurantSpecificDiscounts,
} from './itemFormatFunctions'
import { getTaxes } from './priceFunctions'
import { Platform } from 'react-native'
import { DATABASE_NAME } from '@env'
import { doc, collection } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { add, sub } from 'date-fns'
import { GiftCardOptions, OrderStatus, ProductType } from '../../GiftCards/types'
import { cloudFunction } from '../../../firebase/functions'

type OrderResponse = {
	data: {
		data: {
			created: boolean
			orderNumber: number
			orderId: string
			productId: string
			productDetails: {
				amount: number
			}
		}
		isProductOrder: boolean
		productType: string
	}
}

// All monetary values are in cents.
interface ProcessProductOrderArgs {
	uId: string
	orgId: string
	prices: any
	items: any[]
	isGuestCheckout: boolean
	isDineIn: boolean
	posExtraData: any
	timestampData: any
	loadedData: any
	isDelivery: boolean
	localTimezone: string
	isSandbox: boolean
	type: ProductType
	product: {
		name: string
		price: number
		quantity: number
		productDetails: GiftCardOptions
	}
	selectedMethods: {
		email: boolean
		textMessage: boolean
		print: boolean
		emailString: string
		phoneString: string
		textUpdates: boolean
	}
	FCMToken: string
	paymentProvider: string
	// Assigned or created in the function call.
	orderId?: string
	date?: string
	status?: OrderStatus
	isProductOrder?: boolean
	receiptOptions?: string[]
	checkoutName: string
}

/**
 * Place order functions
 */
const functions = getFunctions()

const PRICE_ARRAY_PROPS = [
	'taxesList',
	'restaurantTaxesList',
	'restaurantTaxTotals',
	'itemTaxesList',
	'itemTaxTotals',
]

export const findUserID = (userId, isGuestCheckout) => {
	// userId needs to be refactored out of ConfirmOrder.tsxy
	var uId = ''
	if (global.kiosk) {
		uId = userId
	} else if (!isGuestCheckout) {
		uId = auth.currentUser.uid
	} else {
		if (userId !== '') {
			uId = userId
		} else {
			uId = 'Guest'
		}
	}

	return uId
}

function buildChannelData(posExtraData, isDineIn, isDelivery) {
	let channelData = null
	if (global.pos && posExtraData.type === 'thirdParty') {
		channelData = {
			orderType: isDineIn ? 'dineIn' : 'takeOut',
			appType: 'pos',
			posPaymentType: posExtraData.type,
			thirdPartyPaymentInfo: {
				name: posExtraData.name,
				id: posExtraData.firebaseId,
				paymentNote: posExtraData.note,
			},
		}
	} else if (global.pos) {
		channelData = {
			orderType: isDineIn ? 'dineIn' : 'takeOut',
			appType: 'pos',
			posPaymentType: posExtraData.type,
		}
	} else {
		channelData = {
			orderType: isDineIn ? 'dineIn' : isDelivery ? 'delivery' : 'takeOut',
			appType: Platform.OS === 'web' ? 'web' : global.kiosk ? 'kiosk' : 'mobile',
		}
	}
	return channelData
}

function buildReceiptOptions(selectedMethods): string[] {
	const receiptOptions = []
	if (selectedMethods.email) receiptOptions.push('email')
	if (selectedMethods.textMessage) receiptOptions.push('sms')
	if (selectedMethods.print) receiptOptions.push('print')
	return receiptOptions
}

export async function processOrder({
	paymentProvider,
	prices,
	items,
	orderNote,
	discount,
	tip,
	userId,
	isGuestCheckout,
	restaurants,
	tableNumber,
	isDineIn,
	pickupName,
	restaurantDiscountAmount,
	selectedMethods,
	posExtraData,
	redeemedRewards,
	pointsEarned,
	timestampData,
	pickupPointObject,
	isSandbox,
	cartPrep,
	FCMToken,
	loadedData,
	isDelivery,
	restaurantIds,
	deliveryOrgReceivedPercent,
	localTimezone,
	userLocation,
	typePOS,
	currency,
}) {
	const orderLineItems = formatItemsForSquare(items, currency)

	//item data for db orders collection
	const dbOrderItems = formatItemsForFirebase(items, currency)

	const discountObj = getRestaurantSpecificDiscounts(items, prices, currency, [], [])

	const taxArray = getTaxes(items)

	const discountView = formatDiscountForSquare(
		items,
		restaurants,
		discount ? discount.orgCoverage : 0,
		discount,
		prices.subTotal,
		prices.noCodeDiscount,
		prices.discount,
		currency
	)

	const totalServiceFee =
		prices.serviceCharge + prices.serviceChargeGST + prices.serviceChargePST + prices.serviceFeeTax
	const serviceChargeView = formatServiceChargesForSquare(tip, totalServiceFee, currency)

	let displayName = auth.currentUser ? auth.currentUser.displayName : 'Guest'
	if (displayName == null) {
		displayName = 'APP ORDER'
	}

	const note = orderNote
	let orderSource = 'App Pickup'
	if (Platform.OS === 'web') {
		orderSource = 'Web Pickup'
	} else if (global.kiosk) {
		if (isDineIn) {
			orderSource = 'Dine In'
		} else {
			orderSource = 'Kiosk Pickup'
		}
	}

	const id = doc(collection(db, DATABASE_NAME, global.org, 'Orders')).id
	const priceReturn: any = { ...prices }

	delete priceReturn.noCodeDiscount
	delete priceReturn.deliveryFee

	for (const key in priceReturn) {
		if (!PRICE_ARRAY_PROPS.includes(key)) {
			if (key == 'discount') {
				//if discount, get rid of negative
				priceReturn[key] = Math.abs(Math.round(priceReturn[key] * 100))
			} else {
				priceReturn[key] = Math.round(priceReturn[key] * 100)
			}
		} else {
			//if taxes list, go through each entry
			if (priceReturn[key].length > 0) {
				priceReturn[key] = priceReturn[key].map(tax => ({
					...tax,
					amount: Math.round(tax.amount),
				}))
			}
		}
	}

	priceReturn.currency = currency.toUpperCase()
	priceReturn.subtotal = priceReturn.subTotal
	priceReturn.subtotalAfterDiscount = priceReturn.subTotalAfterDiscount
	delete priceReturn.subTotal
	delete priceReturn.subTotalAfterDiscount

	const uId = findUserID(userId, isGuestCheckout)

	const channelData = buildChannelData(posExtraData, isDineIn, isDelivery)

	if (pickupPointObject && pickupPointObject.id) {
		channelData.pickupPointId = pickupPointObject.id
	}

	let loyaltyData = {}
	if (pointsEarned > 0 && auth.currentUser && !global.kiosk) {
		loyaltyData = {
			userId: uId,
			pointsEarned: pointsEarned,
		}
	}

	let pickupDate = null
	if (isDelivery && cartPrep.isScheduled) {
		pickupDate = sub(new Date(cartPrep?.deliveryTime), {
			minutes: cartPrep?.deliveryPrepTime - cartPrep?.longestPrepTime,
		})?.toISOString?.()
	} else if (cartPrep.isScheduled) {
		pickupDate = cartPrep?.pickupTime
	} else {
		pickupDate = add(new Date(), { minutes: cartPrep?.longestPrepTime })?.toISOString?.()
	}

	const receiptOptions = buildReceiptOptions(selectedMethods)

	const orderObj = {
		id: id,
		status: 'Pending',
		date: new Date()?.toISOString?.(),
		pickupDate: pickupDate,
		prepTime: cartPrep.prepTimeDuration,
		type: channelData.orderType || 'takeOut',
		source: {
			channel: 'nextgen',
			type: channelData.appType || 'unknown',
		},
		note: note,
		customer: {
			coordinates: userLocation.coordinates,
			uid: auth.currentUser ? uId : null,
			registered: auth.currentUser && !global.kiosk ? true : false,
			name: auth.currentUser && !global.kiosk ? auth.currentUser.displayName : pickupName,
			email: selectedMethods.emailString || '',
			phoneNumber: selectedMethods.phoneString ? '1' + selectedMethods.phoneString : '',
		},
		notifications: {
			sms: {
				send: selectedMethods.textUpdates && (FCMToken === null || !FCMToken) ? true : false,
			},
			push: {
				send: selectedMethods.textUpdates && FCMToken !== null && FCMToken ? true : false,
				FCMToken: FCMToken,
			},
		},
		promos: discount
			? [
					{
						amount: priceReturn.discount,
						code: discount.couponCode,
						promoId: discount.id,
						name: discount.name,
						type: discount.type,
					},
				]
			: [],
		receiptOptions: receiptOptions,
		orderItems: dbOrderItems,
		transaction: priceReturn,
		loyaltyData: loyaltyData,
		timestamps: timestampData,
		scheduled: cartPrep.isScheduled,
		isTest: isSandbox,
		tableNumber: tableNumber !== '' && tableNumber !== null ? 'table ' + tableNumber : pickupName,

		// Adding this payment object to the cash payment only so that the payment gets confirmed
		...(typePOS === 'cash' && {
			payment: {
				amount: prices.total * 100,
				currency: currency.toUpperCase(),
			},
		}),
	}

	const phoneNumber = selectedMethods.phoneString !== '' ? '+1' + selectedMethods.phoneString : ''

	let orderProcessed: any = await cloudFunction('v2_processCustomerOrder', {
		prepTime: cartPrep.prepTimeDuration,
		paymentProvider,
		orderNote: note,
		orderItems: orderLineItems,
		dbOrderItems: dbOrderItems,
		discounts: discountView,
		serviceCharges: serviceChargeView,
		source: orderSource,
		uid: userId,
		orgId: global.org,
		taxes: taxArray,
		orderId: id,
		orgDiscountPercentCoverage: discount ? discount.orgCoverage : 0,
		restaurantSpecificDiscountObj: discountObj,
		phoneNumber: phoneNumber,
		tableNumber: tableNumber,
		isDineIn: isDineIn,
		tipAmount: prices.tip,
		priceData:
			global.kiosk && paymentProvider === 'square' ? { ...prices, serviceCharge: 0 } : prices,
		pickupName: pickupName,
		discountId: discount ? discount.id : '',
		orderObj: orderObj,
		isGuestCheckout: isGuestCheckout,
		redeemedRewards: redeemedRewards,
		isKiosk: global.kiosk,
		isBankersRounding: loadedData[global.org].taxRoundingMethod === 'bankers' || false,
		deliveryOrgReceivedPercent: deliveryOrgReceivedPercent,
		isDelivery: isDelivery,
		restaurantDiscountAmounts: restaurantDiscountAmount,
		timezone: localTimezone,
		isProductOrder: false,
		userLocation,
	}).catch(e => {
		console.log(e)
		return 'PaymentError'
	})

	orderProcessed =
		orderProcessed === 'PaymentError'
			? 'PaymentError'
			: { ...orderProcessed, orderNumDisplay: orderProcessed.orderNumber, created: true }

	return orderProcessed
}

export async function addCouponForReferrer(couponCode, userId) {
	if (couponCode.startsWith('refer-')) {
		const addReferrerCoupon = httpsCallable(functions, 'v2_addCouponForReferrer')

		addReferrerCoupon({
			orgId: global.org,
			userId: userId,
			couponCode: couponCode,
		})
			.then(() => {
				console.log('Referrer coupon added')
			})
			.catch(e => {
				console.log('Error adding referrer coupon ' + e)
			})
	}
}

export async function processProductOrder(orderData) {
	console.log(`[Checkout.Helpers.functions.processProductOrder] Processing product order.`)

	const orderRef = doc(collection(db, DATABASE_NAME, global.org, 'Orders'))
	const { posExtraData, isDineIn, isDelivery } = orderData
	const channelData = buildChannelData(posExtraData, isDineIn, isDelivery)

	orderData.id = orderRef.id
	orderData.organizationId = global.org
	orderData.type = 'digital'
	orderData.source = {
		channel: 'nextgen',
		type: channelData.appType || 'unknown',
	}
	orderData.tableNumber = orderData.pickupName
	orderData.timestamps = orderData.timestampData
	orderData.customer = {
		uid: auth.currentUser ? orderData.uId : null,
		registered: auth.currentUser && !global.kiosk ? true : false,
		name: auth.currentUser && !global.kiosk ? auth.currentUser.displayName : orderData.pickupName,
		email: orderData.selectedMethods.emailString || '',
		phoneNumber: orderData.selectedMethods.phoneString
			? '1' + orderData.selectedMethods.phoneString
			: '',
	}
	orderData.isTest = orderData.isSandbox
	orderData.notifications = {
		sms: {
			send:
				orderData.selectedMethods.textUpdates &&
				(orderData.FCMToken === null || !orderData.FCMToken)
					? true
					: false,
		},
		push: {
			send:
				orderData.selectedMethods.textUpdates && orderData.FCMToken !== null && orderData.FCMToken
					? true
					: false,
			FCMToken: orderData.FCMToken,
		},
	}

	delete orderData.timestampData
	delete orderData.posExtraData
	delete orderData.isSandbox
	delete orderData.isDelivery
	delete orderData.isDineIn
	delete orderData.isProductOrder
	delete orderData.items
	delete orderData.paymentProvider
	delete orderData.tip
	delete orderData.uId
	delete orderData.FCMToken
	delete orderData.cartPrep
	delete orderData.deliveryOrgReceivedPercent
	delete orderData.discount
	delete orderData.restaurants
	delete orderData.restaurantIds
	delete orderData.restaurantDiscountAmount
	delete orderData.userId
	delete orderData.isGuestCheckout
	delete orderData.orderNote
	delete orderData.pickupPointObject
	delete orderData.redeemedRewards
	delete orderData.selectedMethods

	const processProductOrder = httpsCallable(functions, 'v2_processProductOrder')
	const orderProcessedResult = await processProductOrder(orderData).catch(e => {
		console.log(e)
		return 'PaymentError'
	})

	const orderProcessed = orderProcessedResult as unknown as OrderResponse
	if (typeof orderProcessed == 'string') return orderProcessed

	const { data, isProductOrder, productType } = orderProcessed.data
	const response = {
		data,
		orderNumDisplay: data.orderNumber,
		orderId: data.orderId,
		orderNumber: data.orderNumber,
		created: data.created,
		isProductOrder,
		productType,
	}

	return response
}
